import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, User, Building } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';

interface SearchResult {
  id: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  primary_email?: string;
}

interface ContactSearchDropdownProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClearSearch?: () => void;
  onSearchSelect?: () => void;
  className?: string;
  showOnAllPages?: boolean;
}

// Implement debounce function to limit search frequency
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

export function ContactSearchDropdown({
  searchValue,
  onSearchChange,
  onClearSearch,
  onSearchSelect,
  className = "",
}: ContactSearchDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(searchValue || '');
  const { currentWorkspace } = useWorkspaces();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastSearchRef = useRef<string>('');
  
  // Debounce the search value to prevent too many API calls
  const debouncedSearchValue = useDebounce(inputValue, 400);
  
  // Maximum retries for search requests
  const MAX_RETRIES = 2;
  const [retryCount, setRetryCount] = useState(0);

  // Handle clicks outside the dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      console.log('Click outside check', {
        dropdownRef: !!dropdownRef.current,
        contains: dropdownRef.current?.contains(event.target as Node),
      });
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search contacts with retry logic
  const searchContacts = useCallback(async (searchTerm: string) => {
    if (!currentWorkspace?.id || !searchTerm.trim()) {
      console.log('Skipping search - no workspace or empty search term');
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Don't re-run the same search query
    if (lastSearchRef.current === searchTerm) {
      console.log('Skipping duplicate search for:', searchTerm);
      return;
    }
    
    console.log('Searching contacts for:', searchTerm, 'in workspace:', currentWorkspace.id);
    lastSearchRef.current = searchTerm;
    setLoading(true);
    setError(null);

    try {
      console.log('Calling search_contacts_ranked RPC...');
      const { data, error } = await supabase.rpc('search_contacts_ranked', {
        search_term: searchTerm.trim(),
        workspace_id_param: currentWorkspace.id,
        page_size: 15,
        page_number: 1
      });

      if (error) {
        console.error('Search error response:', error);
        throw error;
      }
      
      console.log('Search results:', data?.length, 'results');
      setResults(data || []);
      setIsOpen(true);
      
    } catch (error) {
      console.error('Error searching contacts:', error);
      
      // Implement retry logic for network errors
      if (retryCount < MAX_RETRIES && isRetryableError(error)) {
        const nextRetry = retryCount + 1;
        setRetryCount(nextRetry);
        
        // Add exponential backoff
        const delay = Math.min(200 * Math.pow(2, nextRetry - 1), 2000);
        console.log(`Retrying search (${nextRetry}/${MAX_RETRIES}) after ${delay}ms`);
        setTimeout(() => {
          searchContacts(searchTerm);
        }, delay);
        
        return;
      }
      
      setError('Failed to search contacts');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, retryCount]);

  // Use debounced value for search - memoize the function to avoid unnecessary re-renders
  useEffect(() => {
    // Avoid excessive logging for performance reasons
    if (debouncedSearchValue) {
      searchContacts(debouncedSearchValue);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [debouncedSearchValue, searchContacts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log('Input changed to:', newValue);
    
    // Update local state
    setInputValue(newValue);
    
    // Update parent state
    onSearchChange(newValue);
  };

  const handleInputFocus = () => {
    console.log('Input focused, current value:', inputValue);
    if (inputValue.trim()) {
      setIsOpen(true);
    }
  };

  const handleClear = () => {
    console.log('Clearing search');
    setInputValue('');
    onSearchChange('');
    setIsOpen(false);
    lastSearchRef.current = '';
    if (onClearSearch) {
      onClearSearch();
    }
    inputRef.current?.focus();
  };

  const handleContactSelect = (contactId: string) => {
    console.log('Contact selected:', contactId);
    setIsOpen(false);
    
    const currentPath = location.pathname;
    const isContactDetailsPage = /^\/contacts\/[^/]+$/.test(currentPath);
    
    if (isContactDetailsPage && currentPath !== `/contacts/${contactId}`) {
      navigate(`/contacts/${contactId}`, { replace: true });
    } else {
      navigate(`/contacts/${contactId}`);
    }
    
    if (onSearchSelect) {
      onSearchSelect();
    }
  };

  // Helper function to determine if an error is retryable
  function isRetryableError(error: any) {
    return (
      error instanceof TypeError && error.message === 'Failed to fetch' ||
      error.status === 0 ||
      error.status >= 500 ||
      error.message?.includes('fetch') ||
      error.message?.includes('network')
    );
  }

  const getDisplayName = (result: SearchResult) => {
    if (result.first_name || result.last_name) {
      return `${result.first_name || ''} ${result.last_name || ''}`.trim();
    }
    return result.company_name || 'Unnamed Contact';
  };

  console.log('Render state:', {
    inputValue, 
    searchValue, 
    isOpen, 
    resultsCount: results.length,
    loading
  });

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search contacts..."
          value={inputValue}
          onChange={handleInputChange}
          className="w-full pl-8 h-9 pr-8"
          onFocus={handleInputFocus}
        />
        {inputValue && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-1/2 transform -translate-y-1/2 h-8 w-8"
            onClick={handleClear}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg p-1 max-h-[300px] overflow-auto">
          {loading ? (
            <div className="p-2 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          ) : error ? (
            <div className="p-2 text-center text-sm text-destructive">
              {error}
            </div>
          ) : results.length > 0 ? (
            <div>
              {results.map((result) => (
                <div
                  key={result.id}
                  className="cursor-pointer hover:bg-accent rounded-md p-2 transition-colors"
                  onClick={() => handleContactSelect(result.id)}
                >
                  <div className="flex items-center gap-2">
                    {result.first_name || result.last_name ? (
                      <User className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Building className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <div className="font-medium">
                        {getDisplayName(result)}
                      </div>
                      {result.company_name && (result.first_name || result.last_name) && (
                        <div className="text-xs text-muted-foreground">
                          {result.company_name}
                        </div>
                      )}
                      {result.primary_email && (
                        <div className="text-xs text-muted-foreground">
                          {result.primary_email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="border-t mt-1 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center text-xs text-primary"
                  onClick={() => {
                    navigate(`/contacts?search=${encodeURIComponent(inputValue)}`);
                    setIsOpen(false);
                  }}
                >
                  Show all results
                </Button>
              </div>
            </div>
          ) : inputValue.trim() ? (
            <div className="p-2 text-center text-sm text-muted-foreground">
              No contacts found
            </div>
          ) : (
            <div className="p-2 text-center text-sm text-muted-foreground">
              Start typing to search
            </div>
          )}
        </div>
      )}
    </div>
  );
}